import React from "react";

import MoneyQuestion from "../../../components/MoneyQuestion";
import RadioQuestion from "../../../components/RadioQuestion";
import TextQuestion from "../../../components/TextQuestion";
import { isMoneyValid } from "../../../shared/utils/moneyUtils";

const FINANCE_OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "unknown", label: "I don’t know" },
];

const REQUIRED = {
  institution: { field: "name", error: "Enter who it’s with" },
  vehicle: { field: "description", error: "Enter the registration number, make or model" },
  valuable: { field: "description", error: "Tell us what it is" },
  owed: { field: "description", error: "Tell us who owes the money" },
};

// The fields each kind's first submission is built from. A vehicle's finance goes as a second
// submission, so its questions stay open for a retry after the vehicle itself has gone.
const FIRST_SUBMISSION_FIELDS = {
  institution: ["name", "reference"],
  vehicle: ["description"],
  valuable: ["description", "value"],
  owed: ["description", "value"],
};

// The fields each kind of item asks for, by name, in the order they appear
export const FIELD_ORDER = ["type", "name", "reference", "description", "finance", "lender", "value"];

// kind can be known before the type is, when every type on offer asks the same questions
export const validateEstateItem = (type, kind, values) => {
  const errors = {};
  if (type == null) {
    errors.type = "Select what it is";
  }
  if (kind == null) {
    return errors;
  }
  const { field, error } = REQUIRED[kind];
  if (!values[field]?.trim()) {
    errors[field] = error;
  }
  if ((kind === "valuable" || kind === "owed") && values.value?.trim() && !isMoneyValid(values.value)) {
    errors.value = "Enter an amount, like £1,500";
  }
  return errors;
};

// The questions for one kind of item, from prototype v5. fieldRefs holds each question's wrapper
// so the page can move focus to the first one with an error. Once the item's first submission has
// gone, locked disables its questions so a retry can't send a second, different item.
const EstateItemFields = ({ kind, type, nameHint, values, errors, onChange, fieldRefs, locked = false }) => {
  const lockedFields = locked ? (FIRST_SUBMISSION_FIELDS[kind] ?? []) : [];
  const question = (name, element) => (
    <div key={name} ref={(el) => (fieldRefs.current[name] = el)}>
      {element}
    </div>
  );
  const text = (name, props) =>
    question(
      name,
      <TextQuestion
        name={name}
        value={values[name]}
        valueEntered={onChange(name)}
        validationError={errors[name]}
        autoComplete="off"
        disabled={lockedFields.includes(name)}
        {...props}
      />,
    );
  const money = (name, label) =>
    question(
      name,
      <MoneyQuestion
        question={label}
        name={name}
        value={values[name] ?? ""}
        valueEntered={onChange(name)}
        validationError={errors[name]}
        optional
        inputMode="decimal"
        autoComplete="off"
        disabled={lockedFields.includes(name)}
      />,
    );

  switch (kind) {
    case "institution":
      return [
        text("name", { question: "Who is it with?", hint: nameHint }),
        text("reference", {
          question: type?.referenceLabel ?? "Account or policy number",
          hint: "If you don’t have it, leave it blank. We’ll ask them for it.",
          optional: true,
          inputMode: type?.lastFourDigitsOnly ? "numeric" : undefined,
        }),
      ];
    case "vehicle":
      return [
        text("description", { question: "Registration number, make and model" }),
        question(
          "finance",
          <RadioQuestion
            question="Is there a loan or finance agreement on it?"
            name="finance"
            options={FINANCE_OPTIONS}
            selected={values.finance}
            optionSelected={onChange("finance")}
          />,
        ),
        values.finance === "yes" && text("lender", { question: "Who is the finance with?", optional: true }),
      ];
    case "valuable":
      return [
        text("description", { question: "What is it?", hint: "For example, an engagement ring or a painting" }),
        money("value", "Roughly what is it worth?"),
      ];
    case "owed":
      return [
        text("description", { question: "Who owes the money, and what for?" }),
        money("value", "Roughly how much?"),
      ];
    default:
      return null;
  }
};

export default EstateItemFields;
